import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TimezoneSelect, { browserTimezone, fmtSlotInTz } from "../components/TimezoneSelect";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:30801");

async function fetchOffer(token: string) {
  const r = await fetch(`${API_BASE}/api/public/interview-slots/${token}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function postConfirm(token: string, slotIndex: number) {
  const r = await fetch(`${API_BASE}/api/public/interview-slots/${token}/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slot_index: slotIndex }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error((err as any).detail || "Failed to confirm");
  }
  return r.json();
}

export default function InterviewConfirmPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState<{ slot: string; location?: string } | null>(null);
  const [error, setError] = useState("");
  const [viewTz, setViewTz] = useState(browserTimezone());

  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: ["slotOffer", token],
    queryFn: () => fetchOffer(token!),
    enabled: !!token,
    retry: 1,
  });
  const d = rawData as any;

  // Auto-confirm when candidate clicks a specific slot link from their email (?slot=N)
  const slotParam = parseInt(searchParams.get("slot") || "0");
  useEffect(() => {
    if (!d || !slotParam || confirmed || confirming) return;
    const slots: string[] = d.slots?.length ? d.slots : [d.slot_1, d.slot_2, d.slot_3].filter(Boolean);
    const slotTime = slots[slotParam - 1];
    if (slotTime && (d.status === "awaiting_candidate" || d.status === "pending")) {
      confirm(slotParam, slotTime);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  // Normalise slot list: backend may return `slots[]` (new) or `slot_1/2/3` (old)
  const slots: string[] = d
    ? (d.slots?.length ? d.slots : [d.slot_1, d.slot_2, d.slot_3].filter(Boolean))
    : [];

  // Both old "pending" and new "awaiting_candidate" mean "candidate can pick"
  const canPick = d && (d.status === "awaiting_candidate" || d.status === "pending");

  async function confirm(slotIndex: number, slotTime: string) {
    setConfirming(true);
    setError("");
    try {
      const res = await postConfirm(token!, slotIndex);
      setConfirmed({ slot: slotTime, location: res.location_or_link || "" });
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again or contact the recruiter.");
    } finally {
      setConfirming(false);
    }
  }

  const card: React.CSSProperties = {
    background: "white", borderRadius: 16, padding: 32,
    boxShadow: "0 4px 32px rgba(0,0,0,0.10)", maxWidth: 520, width: "100%", margin: "0 auto",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
          <img src="/logo-dark.png" alt="HRxAI" style={{ height: 32, width: "auto", objectFit: "contain" }}
            onError={e => { const t = e.target as HTMLImageElement; t.style.display = "none"; }} />
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>Interview Scheduling</div>
      </div>

      {isLoading && (
        <div style={card}>
          <div style={{ textAlign: "center", padding: "32px 0", color: "#64748b" }}>Loading…</div>
        </div>
      )}

      {!isLoading && isError && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Link Not Found</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This invitation link is invalid or has expired. Please contact the recruiter.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d?.status === "awaiting_interviewer" && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Waiting for Interviewer</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>The interviewer hasn't submitted their available slots yet. You'll receive an email as soon as they do.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d?.status === "expired" && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏰</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Link Expired</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This slot offer has expired. Please contact the recruiter to request a new one.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d?.status === "confirmed" && !confirmed && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Already Confirmed</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>You've already selected a slot. Check your email for the calendar invite.</p>
          </div>
        </div>
      )}

      {confirmed && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 22 }}>Interview Confirmed!</h2>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "16px 20px", marginTop: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>{fmtSlotInTz(confirmed.slot, viewTz)}</div>
            </div>
            {confirmed.location && (
              <div style={{ marginBottom: 16 }}>
                {confirmed.location.startsWith("http") ? (
                  <a href={confirmed.location} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 18px", background: "#1a73e8", color: "white", borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                    📹 Join Meeting
                  </a>
                ) : (
                  <div style={{ fontSize: 13, color: "#475569" }}>{confirmed.location}</div>
                )}
              </div>
            )}
            <p style={{ margin: 0, color: "#64748b", fontSize: 13, lineHeight: 1.6 }}>
              A calendar invite has been sent to your email. We look forward to speaking with you!
            </p>
          </div>
        </div>
      )}

      {!isLoading && !isError && canPick && !confirmed && (
        <div style={card}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ margin: "0 0 4px", color: "#1e3a8a", fontSize: 20 }}>
              {d.job_title ? `Interview: ${d.job_title}` : "Interview Invitation"}
            </h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
              Hi {d.candidate_name || "there"} 👋 — please pick one of the available slots below.
              Once confirmed, you'll receive a calendar invite.
            </p>
          </div>

          {/* Timezone selector */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", whiteSpace: "nowrap" }}>View times in:</span>
            <TimezoneSelect value={viewTz} onChange={setViewTz} style={{ flex: 1, minWidth: 200 }} />
          </div>

          {/* Details row */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            {d.interview_type && (
              <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                {String(d.interview_type).replace("_", " ")}
              </span>
            )}
            {d.duration_minutes && (
              <span style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                {d.duration_minutes} min
              </span>
            )}
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {slots.length === 0 && (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "20px 0" }}>
              No slots available yet. Please check back later or contact the recruiter.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {slots.map((slot: string, i: number) => (
              <button
                key={i}
                disabled={confirming}
                onClick={() => confirm(i + 1, slot)}
                style={{
                  width: "100%", padding: "16px 20px", borderRadius: 10,
                  border: "2px solid #bfdbfe",
                  background: confirming ? "#f8fafc" : "white",
                  cursor: confirming ? "not-allowed" : "pointer",
                  textAlign: "left", transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!confirming) (e.currentTarget as HTMLElement).style.background = "#eff6ff"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = confirming ? "#f8fafc" : "white"; }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a", marginBottom: 2 }}>Option {i + 1}</div>
                <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 600 }}>{fmtSlotInTz(slot, viewTz)}</div>
              </button>
            ))}
          </div>

          {confirming && (
            <div style={{ textAlign: "center", marginTop: 20, color: "#64748b", fontSize: 13 }}>Confirming your selection…</div>
          )}
        </div>
      )}
    </div>
  );
}
